// The upsell pages (ops plan 2026-10-13-billing-system §4.5, §4.7, slice 6): a page of a feature the location's plan
// doesn't include, or an add-on paused after a failed card, shows legacy's welcome page instead (gift-cards/welcome,
// online-store/welcome, employees/timecards/welcome, which no legacy link reached) with the plan's own monthly price and
// "Contact your Account Representative". Only when the location's entitlements are enforced (a real plan, not Legacy):
// on Legacy, while they load, or when they can't be read, the page shows as it always has (fail open).
import type { ReactNode } from 'react';
import { FEATURE_UPSELL } from '@radish/contracts';
import { offerFor } from '@radish/core/billing';
import { formatMoney } from '../../lib/format.ts';
import { useActiveLocation } from '../../app/location.tsx';
import { useEntitlements } from '../../data/entitlements.ts';

/** "$19/mo" (legacy's welcome pages wrote whole dollars), "$19.50/mo" otherwise. */
const perMonth = (text: string): string => `${text.replace(/\.00$/, '')}/mo`;

function UpsellPage({ featureKey }: { readonly featureKey: string }) {
  const location = useActiveLocation();
  const { facts } = useEntitlements(location.locationId);
  const offer = offerFor(facts, featureKey);
  const copy = FEATURE_UPSELL[featureKey];
  const name = offer?.name ?? copy?.title ?? featureKey;
  const paused = facts?.paused.includes(featureKey) === true;
  return (
    <div data-testid="upsell-page" data-feature={featureKey} className="flex min-h-[400px] flex-col items-center justify-center gap-4 px-4 text-center">
      <h2 className="text-[22px] font-semibold text-text-1">{copy?.title ?? name}</h2>
      {paused ? (
        <p data-testid="upsell-paused" className="max-w-lg text-[15px] text-text-2">
          {`${name} is paused until your Radish bill is paid. Selling, payments, kitchen screens and reports keep working.`}
        </p>
      ) : (
        <p className="max-w-lg text-[15px] text-text-2">{copy?.headline ?? `${name} is not on this location's plan.`}</p>
      )}
      {offer === null ? null : (
        <p data-testid="upsell-offer" className="text-[16px] font-bold text-primary">{`${copy?.cta ?? `Add ${name}`} for ${perMonth(formatMoney(offer.price))}`}</p>
      )}
      <p className="text-[14px] text-text-3">Contact your Account Representative to add it.</p>
    </div>
  );
}

/** The page itself when the feature is on at the location, else its upsell. */
export function FeatureGate({ featureKey, children }: { readonly featureKey: string; readonly children: ReactNode }) {
  const location = useActiveLocation();
  const { standing } = useEntitlements(location.locationId);
  return standing(featureKey) === 'on' ? <>{children}</> : <UpsellPage featureKey={featureKey} />;
}
